import { useEffect, useRef, useState } from "react";
import type { TranscribeStatusDTO } from "../../shared/types";
import { get, postFile } from "../lib/api";
import { IconButton } from "./IconButton";

/**
 * The mic beside a text box: press to record, press again to stop. The clip goes to the platform, which transcribes
 * it and answers with the words; they land in the box to read and edit, and only that text is ever sent. The audio
 * is not stored anywhere — not here, not on the platform.
 */

const OFF: TranscribeStatusDTO = { available: false, engine: null, why: null, maxSeconds: 60, maxBytes: 5 * 1024 * 1024 };

/** Whether the platform can turn speech into words right now. Asked once per screen that offers a mic. */
export function useTranscription(): TranscribeStatusDTO | null {
  const [status, setStatus] = useState<TranscribeStatusDTO | null>(null);
  useEffect(() => {
    get<TranscribeStatusDTO>("/api/transcribe").then(setStatus, () => setStatus(OFF));
  }, []);
  return status;
}

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

export function VoiceNote({ maxSeconds, disabled, onText, onBusy }: {
  maxSeconds: number;
  disabled?: boolean;
  /** The transcribed words, to append to whatever is in the box. */
  onText: (text: string) => void;
  /** While recording or transcribing, so the sheet can hold its buttons. */
  onBusy?: (busy: boolean) => void;
}) {
  const [state, setState] = useState<"idle" | "recording" | "working">("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsed = useRef(0);

  useEffect(() => onBusy?.(state !== "idle"), [state, onBusy]);
  // A page left mid-recording never leaves the microphone on.
  useEffect(() => () => {
    if (timer.current) clearInterval(timer.current);
    recorder.current?.stream.getTracks().forEach((t) => t.stop());
  }, []);

  const stop = () => recorder.current?.state === "recording" && recorder.current.stop();

  const start = async () => {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Refused, or no microphone at all: say it plainly and leave the box alone.
      return setError("The browser did not give us the microphone — type it instead.");
    }
    const chunks: Blob[] = [];
    const rec = new MediaRecorder(stream);
    recorder.current = rec;
    rec.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      if (timer.current) clearInterval(timer.current);
      setState("working");
      try {
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const { text } = await postFile<{ text: string }>("/api/transcribe", new File([blob], "voice-note", { type: blob.type }));
        if (text) onText(text);
        else setError("Nothing was said in that recording.");
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setState("idle");
        setSeconds(0);
      }
    };
    rec.start();
    setState("recording");
    setSeconds(0);
    elapsed.current = 0;
    timer.current = setInterval(() => {
      setSeconds((elapsed.current += 1));
      // Stops by itself at the limit, so a recording is never refused for being too long.
      if (elapsed.current >= maxSeconds) stop();
    }, 1000);
  };

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <IconButton
        size="lg"
        fill={state === "recording" ? "red" : "white"}
        label={state === "recording" ? "Stop recording" : "Record a voice note"}
        disabled={disabled || state === "working"}
        onClick={() => (state === "recording" ? stop() : start())}
      >
        {state === "recording" ? <span className="size-3.5 rounded-[3px] bg-white" /> : <MicIcon working={state === "working"} />}
      </IconButton>
      <span className="min-w-0 text-sm text-muted">
        {state === "recording" ? (
          <span className="inline-flex items-center gap-2">
            <span className="size-2 animate-pulse rounded-full bg-danger" />
            <span className="font-mono tabular-nums">{clock(seconds)}</span>
            <span className="hidden sm:inline">· tap to stop</span>
          </span>
        ) : state === "working" ? (
          "Turning it into words…"
        ) : (
          "Or say it"
        )}
      </span>
      {error && <span className="w-full text-sm text-danger">{error}</span>}
    </div>
  );
}

function MicIcon({ working }: { working: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" className={`size-5 ${working ? "animate-pulse" : ""}`} aria-hidden>
      <path d="M12 4a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V7a3 3 0 0 1 3-3ZM6 11a6 6 0 0 0 12 0M12 17v3" />
    </svg>
  );
}

/** The mic under a note, when the platform can transcribe: the words land in the note; nothing is said when it cannot. */
export function NoteMic({ disabled, onText, onBusy }: { disabled?: boolean; onText: (text: string) => void; onBusy?: (busy: boolean) => void }) {
  const transcription = useTranscription();
  if (!transcription?.available) return null;
  return <VoiceNote maxSeconds={transcription.maxSeconds} disabled={disabled} onBusy={onBusy} onText={onText} />;
}
